/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  LoanSuretyItem,
  fetchSuretyGiven,
  fetchSuretyReceived,
} from "./loanSurety.service";

type SuretyTab = "received" | "given";

const LoanSuretyStatus: React.FC = () => {
  const [searchParams] = useSearchParams();
  const loanType = searchParams.get("loanType") || "";
  const loanLabel = searchParams.get("loanLabel") || "";

  const [activeTab, setActiveTab] = useState<SuretyTab>("received");
  const [receivedData, setReceivedData] = useState<LoanSuretyItem[]>([]);
  const [givenData, setGivenData] = useState<LoanSuretyItem[]>([]);
  const [loading, setLoading] = useState(!!loanType);

  const loadData = useCallback(async () => {
    if (!loanType) return;
    setLoading(true);
    try {
      if (activeTab === "received") {
        const list = await fetchSuretyReceived(loanType);
        setReceivedData(Array.isArray(list) ? list : []);
      } else {
        const list = await fetchSuretyGiven(loanType);
        setGivenData(Array.isArray(list) ? list : []);
      }
    } catch (err: any) {
      if (activeTab === "received") setReceivedData([]);
      else setGivenData([]);
      toast.error(err?.message || "Loan Surety API Error");
    } finally {
      setLoading(false);
    }
  }, [activeTab, loanType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const records: LoanSuretyItem[] =
    activeTab === "received" ? receivedData : givenData;
  const hasRecords = !loading && Array.isArray(records) && records.length > 0;
  const showEmpty = !loading && (!Array.isArray(records) || records.length === 0);

  const renderRow = (label: string, value: string) => (
    <div className="surety-row">
      <span className="surety-label">{label}</span>
      <span className="surety-value">{value || "-"}</span>
    </div>
  );

  return (
    <div className="page-wrapper">
      <style>{statusStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Loan Surety Status</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={all_routes.loanSurety}>Loan Surety</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Status
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <Link
                to={all_routes.loanSurety}
                className="btn btn-outline-secondary d-inline-flex align-items-center me-2"
              >
                <i className="ti ti-arrow-left me-1" />
                Back
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={loadData}
                disabled={loading || !loanType}
                title="Sync"
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  <i className="ti ti-refresh me-2" />
                )}
                Sync
              </button>
            </li>
          </ul>
        </div>

        {(loanType || loanLabel) && (
          <div className="card mb-3">
            <div className="card-body py-3 d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted fs-13">Selected loan type:</span>
              <span className="badge badge-soft-primary rounded-pill px-3 py-2">
                {loanType}
                {loanLabel ? ` - ${loanLabel}` : ""}
              </span>
            </div>
          </div>
        )}

        {!loanType && (
          <div className="alert alert-warning">
            Please select a loan type first.{" "}
            <Link to={all_routes.loanSurety}>Go to selection</Link>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <div className="surety-tab-bar">
              <button
                type="button"
                className={`surety-tab-btn ${activeTab === "received" ? "active" : ""}`}
                onClick={() => setActiveTab("received")}
              >
                <i className="ti ti-arrow-down-left" /> Surety Received
              </button>
              <button
                type="button"
                className={`surety-tab-btn ${activeTab === "given" ? "active" : ""}`}
                onClick={() => setActiveTab("given")}
              >
                <i className="ti ti-arrow-up-right" /> Surety Given
              </button>
            </div>

            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3 mb-0">Loading...</p>
              </div>
            )}

            {hasRecords && (
              <div className="row g-3 pt-3">
                {records.map((item, index) => (
                  <div className="col-12 col-xl-6" key={item.id || index}>
                    <div className="surety-card h-100">
                      <div className="surety-card-accent" />
                      <div className="surety-card-body">
                        <h6 className="mb-2 fw-bold text-dark">
                          Loan Type: {item.loanText || item.loanType || "-"}
                        </h6>
                        <div className="surety-divider" />
                        {renderRow(
                          "Employee ID of First Surety",
                          item.firstSurety?.empId || item.empId
                        )}
                        {renderRow(
                          "Name of Employee ID of First Surety",
                          item.firstSurety?.name || item.empName
                        )}
                        {renderRow(
                          "Employee ID of Second Surety",
                          item.secondSurety?.empId
                        )}
                        {renderRow(
                          "Name of Employee ID of Second Surety",
                          item.secondSurety?.name
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showEmpty && (
              <div className="text-center py-5">
                <span className="surety-empty-icon">
                  <i className="ti ti-file-off" />
                </span>
                <h6 className="mt-3 mb-1 text-dark">No Data Available</h6>
                <p className="text-muted mb-0">
                  No surety records found for the selected loan type.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const statusStyles = `
.surety-tab-bar {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #eef0f4;
  margin-bottom: 8px;
}
.surety-tab-btn {
  border: none;
  background: transparent;
  padding: 10px 16px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.surety-tab-btn.active {
  color: var(--primary, #dc2626);
  border-bottom-color: var(--primary, #dc2626);
}
.surety-card {
  position: relative;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(16,24,40,0.04);
}
.surety-card-accent {
  position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
  background: linear-gradient(180deg, var(--primary, #dc2626), rgba(220,38,38,0.55));
}
.surety-card-body { padding: 18px 20px 16px 24px; }
.surety-divider { height: 1px; background: #E8E8E8; margin-bottom: 10px; }
.surety-row {
  display: flex;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.surety-label { font-weight: 600; min-width: 240px; color: #111827; }
.surety-value { flex: 1; color: #374151; }
.surety-empty-icon {
  width: 72px; height: 72px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: #f3f4f6; color: #9ca3af; font-size: 34px;
}
.badge-soft-primary { background: rgba(59,130,246,0.12); color: #2563eb; font-weight: 600; }
.fs-13 { font-size: 13px; }
`;

export default LoanSuretyStatus;
